import React from 'react'
import styled from 'styled-components'
import { AdvancedDropdown } from '../../../molecules/AdvancedDropdown'
import { formatMetricValue } from '../utils/zoningHelpers'
import {
  MetricLabel,
  MetricValueCell,
  EmptyValue,
  DifferenceCell,
} from './ZoneMetricsTable.styles'
import { EditableMinMaxInput } from './EditableMinMaxInput'
import { VisibilityToggle } from './VisibilityToggle'
import { MetricData } from './utils/metricTableHelpers'

const Row = styled.tr<{ $hidden: boolean }>`
  td {
    padding: ${({ theme }) =>
      `${theme.spacing.small} ${theme.spacing.medium} ${theme.spacing.medium} ${theme.spacing.medium}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
    opacity: ${({ $hidden }) => ($hidden ? 0.45 : 1)};
  }
`

const LabelWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

const VariantSelectWrap = styled.div`
  min-width: 240px;
`

const SubNote = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-top: ${({ theme }) => theme.spacing.xsmall};
`

export interface ConceptMetricRowProps {
  /** Concept label, e.g. "Site coverage" / "Floor-area ratio" */
  conceptLabel: string
  /** All variants of this concept (ios/cos or ius/cus/ibus/im) */
  options: { key: string; label: string; metric?: MetricData }[]
  selectedKey: string
  onVariantChange: (key: string) => void
  isHidden: boolean
  onToggleVisibility: () => void
  hideTooltip?: string
  showTooltip?: string
  subNote?: string
  /** Values for the selected variant */
  currentValue?: number
  minValue?: number
  maxValue?: number
  isMinOverridden?: boolean
  isMaxOverridden?: boolean
  isMinPending?: boolean
  isMaxPending?: boolean
  minSource?: string
  maxSource?: string
  unit?: string
  onValueChange: (metricKey: string, value: number) => void
  onValueClear: (metricKey: string) => void
}

/**
 * Edit-mode row for an exclusive metric concept (occupation / utilization).
 * Replaces the radio list + "None (hide this metric)" rows: the eye toggle
 * controls visibility (like every other row) and a compact selector picks
 * the naming variant (UZ vs COS, AZ/CUS/GFZ/BMZ).
 */
export const ConceptMetricRow: React.FC<ConceptMetricRowProps> = ({
  conceptLabel,
  options,
  selectedKey,
  onVariantChange,
  isHidden,
  onToggleVisibility,
  hideTooltip,
  showTooltip,
  subNote,
  currentValue,
  minValue,
  maxValue,
  isMinOverridden = false,
  isMaxOverridden = false,
  isMinPending = false,
  isMaxPending = false,
  minSource,
  maxSource,
  unit,
  onValueChange,
  onValueClear,
}) => {
  const diff =
    maxValue !== undefined && currentValue !== undefined
      ? maxValue - currentValue
      : undefined

  return (
    <Row $hidden={isHidden}>
      <td>
        <LabelWrap>
          <VisibilityToggle
            isHidden={isHidden}
            onToggle={onToggleVisibility}
            hideTooltip={hideTooltip}
            showTooltip={showTooltip}
          />
          <MetricLabel>{conceptLabel}</MetricLabel>
          <VariantSelectWrap>
            <AdvancedDropdown
              value={selectedKey}
              onChange={(value) => {
                if (typeof value === 'string') onVariantChange(value)
              }}
              options={options.map((option) => ({
                id: option.key,
                label: option.label,
              }))}
              disabled={isHidden}
              size="small"
            />
          </VariantSelectWrap>
        </LabelWrap>
        {subNote && !isHidden && <SubNote>{subNote}</SubNote>}
      </td>

      <MetricValueCell>
        {currentValue !== undefined ? (
          formatMetricValue(currentValue, unit, selectedKey)
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </MetricValueCell>

      <MetricValueCell>
        {isHidden ? (
          <EmptyValue>-</EmptyValue>
        ) : (
          <EditableMinMaxInput
            metricKey={selectedKey}
            minValue={minValue}
            maxValue={maxValue}
            isMinOverridden={isMinOverridden}
            isMaxOverridden={isMaxOverridden}
            isMinPending={isMinPending}
            isMaxPending={isMaxPending}
            minSource={minSource as any}
            maxSource={maxSource as any}
            onValueChange={onValueChange}
            onValueClear={onValueClear}
          />
        )}
      </MetricValueCell>

      <DifferenceCell $isPositive={(diff ?? 0) >= 0}>
        {diff !== undefined && !isHidden ? (
          <>
            {diff >= 0 && '+'}
            {formatMetricValue(diff, unit, selectedKey)}
          </>
        ) : (
          <EmptyValue>-</EmptyValue>
        )}
      </DifferenceCell>
    </Row>
  )
}
